import { GraduationCap, Stethoscope, HeartPulse, FileText, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { CONTENT } from "@/content";

const HIGHLIGHTS = [
  { icon: GraduationCap, title: "Infirmière D.E. — 2009", text: "DU techniques de soins en réanimation, faculté de médecine de Montpellier." },
  { icon: HeartPulse, title: "15 ans dans le soin", text: "Réanimation, oncologie pédiatrique, urgences, dialyse, libéral." },
  { icon: Stethoscope, title: "Ostéopathe D.O.", text: "Cinq ans d'études à Eurostéo Meyreuil, en parallèle de mon activité d'infirmière." },
];

const CHAPTERS = [
  {
    title: "Mes débuts dans le soin",
    paragraphs: [
      "Diplômée d'État en soins infirmiers en 2009, j'ai débuté ma carrière dans des services hospitaliers particulièrement exigeants.",
      "J'ai notamment exercé pendant 5 ans en réanimation et en soins intensifs, une expérience qui m'a permis d'acquérir une connaissance approfondie du corps humain, de développer mon sens de l'observation et d'apprendre à faire face à des situations complexes.",
    ],
  },
  {
    title: "Explorer d'autres horizons",
    paragraphs: [
      "Au fil des années, j'ai souhaité enrichir mon expérience en découvrant différentes facettes du métier infirmier : oncologie pédiatrique, urgences, dialyse, puis libéral.",
      "Ces environnements très différents m'ont appris à adapter ma façon de soigner à chaque personne, et ont renforcé une conviction qui guide encore ma pratique aujourd'hui : derrière chaque symptôme se trouve avant tout une personne, avec son histoire et ses besoins propres.",
    ],
  },
  {
    title: "De l'infirmière à l'ostéopathe",
    paragraphs: [
      "En 2018, une réflexion a progressivement fait évoluer ma manière d'envisager le soin : « Et si l'on regardait le corps dans sa globalité ? » Cette question m'a conduite vers l'ostéopathie.",
      "J'ai alors entrepris 5 années d'études en ostéopathie à Eurostéo Meyreuil, parallèlement à mon activité d'infirmière. Cinq ans plus tard, je suis devenue ostéopathe diplômée, sans pour autant abandonner mon premier métier.",
    ],
    memoire: true,
  },
  {
    title: "Aujourd'hui : une double approche",
    paragraphs: [
      "J'exerce aujourd'hui mes deux métiers, infirmière et ostéopathe, qui nourrissent chacun ma façon d'accompagner mes patients : la culture médicale, la rigueur et l'écoute de l'une, le regard global du corps de l'autre.",
      "Cette double expérience me permet d'aborder chaque consultation avec un regard à la fois clinique, global et humain.",
    ],
  },
];

const About = () => (
  <section id="a-propos" className="grain bg-sage-pale py-28 md:py-36" data-testid="about-section">
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:px-12">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-32">
          <Reveal>
            <p className="text-xs font-medium uppercase tracking-[0.28em] text-gold">Qui suis-je ?</p>
            <h2 className="mt-5 font-serif text-4xl font-light leading-tight tracking-tight text-anthracite md:text-5xl">
              De l'hôpital à l'ostéopathie : <em className="text-sage-dark">mon chemin dans le soin</em>
            </h2>
            <div className="gold-rule mt-8 w-24" />
            <p className="mt-8 max-w-md text-base font-light leading-relaxed text-taupe">
              Je suis Sabine Sissan, ostéopathe D.O. et infirmière diplômée
              d'État. Le soin m'accompagne depuis plus de 15 ans, avec un même
              fil conducteur : comprendre la personne dans sa globalité et lui
              proposer une prise en charge attentive, rigoureuse et humaine.
            </p>
          </Reveal>

          <Reveal delay={0.15} className="relative mt-14" data-testid="about-visual">
            <div className="blob absolute -left-8 -bottom-8 h-full w-full bg-beige/80" />
            <img
              src={CONTENT.images.portrait}
              alt={CONTENT.images.portraitAlt}
              loading="lazy"
              className="soft-shadow relative aspect-[4/5] w-full rounded-xl object-cover"
            />
            <div className="absolute -right-4 -top-4 rounded-lg border border-sage-light/60 bg-ivory/95 px-5 py-4 backdrop-blur-sm md:-right-8">
              <p className="font-serif text-lg italic leading-snug text-taupe">
                « Derrière chaque symptôme,<br />il y a d'abord une personne. »
              </p>
            </div>
          </Reveal>

          <div className="mt-14 space-y-6">
            {HIGHLIGHTS.map((h, i) => (
              <Reveal key={h.title} delay={i * 0.1}>
                <div className="flex items-start gap-5" data-testid={`about-highlight-${i}`}>
                  <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-sage-light/70 bg-ivory">
                    <h.icon className="h-5 w-5 text-sage-dark" strokeWidth={1} />
                  </span>
                  <div>
                    <h3 className="font-serif text-xl text-anthracite">{h.title}</h3>
                    <p className="mt-1 text-sm font-light leading-relaxed text-taupe">{h.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      <div className="lg:col-span-7 lg:pt-24">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.08}>
            <div data-testid={`about-chapter-${i}`} className="border-t border-sage-light/60 py-10 first:border-t-0 first:pt-0">
              <h3 className="font-serif text-2xl font-light italic text-anthracite md:text-3xl">{c.title}</h3>
              {c.paragraphs.map((p, j) => (
                <p key={j} className="mt-4 max-w-xl text-base font-light leading-relaxed text-taupe">{p}</p>
              ))}
              {c.memoire && (
                <a
                  data-testid="about-memoire-link"
                  href={CONTENT.memoireUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group mt-6 inline-flex items-center gap-4 rounded-lg border border-sage-light/70 bg-ivory/80 px-6 py-4 transition-all duration-300 hover:border-gold/40"
                >
                  <FileText className="h-5 w-5 shrink-0 text-sage-dark" strokeWidth={1} />
                  <span>
                    <span className="block text-[10px] font-medium uppercase tracking-[0.22em] text-gold">Pour aller plus loin</span>
                    <span className="mt-0.5 block font-serif text-lg italic text-anthracite">Lire mon mémoire de fin d'études</span>
                    <span className="mt-1 block text-xs font-light leading-relaxed text-taupe">
                      « L'ostéopathie a-t-elle un impact sur les enfants atteints de TDAH ? » — Eurostéo Meyreuil
                    </span>
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-sage-dark transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1} />
                </a>
              )}
            </div>
          </Reveal>
        ))}

        <Reveal delay={0.1}>
          <div className="mt-6 rounded-xl border border-gold/30 bg-ivory p-9 md:p-12" data-testid="about-philosophy">
            <p className="text-xs font-medium uppercase tracking-[0.28em] text-gold">Ma philosophie</p>
            <p className="mt-6 font-serif text-2xl font-light italic leading-snug text-anthracite md:text-3xl">
              « Chaque patient est unique. »
            </p>
            <p className="mt-6 text-base font-light leading-relaxed text-taupe">
              Je ne souhaite pas réduire une personne à une douleur ou à une
              zone du corps. Ma pratique repose sur l'observation, l'écoute et
              la recherche d'un équilibre adapté à chacun. Je prends le temps
              de comprendre le contexte dans lequel une gêne s'est installée,
              afin de proposer une prise en charge individualisée.
            </p>
            <p className="mt-4 text-base font-light leading-relaxed text-taupe">
              Rigueur, douceur et bienveillance accompagnent ma pratique au
              quotidien. Parce qu'au-delà d'un symptôme, il y a toujours une
              personne, son histoire — et un corps qui cherche constamment à
              s'adapter.
            </p>
            <div className="gold-rule mt-8 w-24" />
            <p className="mt-6 font-serif text-xl italic text-anthracite">
              Sabine Sissan <span className="text-sm not-italic uppercase tracking-[0.2em] text-taupe/70">— Ostéopathe D.O.</span>
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default About;

